import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  Plus, 
  Home, 
  Calendar, 
  DollarSign, 
  Users, 
  Star, 
  TrendingUp,
  Eye,
  Edit,
  Trash2
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import toast from 'react-hot-toast';

const HostDashboard = () => {
  const { user } = useAuth();
  const [listings, setListings] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    totalListings: 0,
    totalBookings: 0,
    totalEarnings: 0,
    averageRating: 0
  });

  useEffect(() => {
    fetchHostData();
  }, []);

  const fetchHostData = async () => {
    try {
      const [listingsRes, bookingsRes] = await Promise.all([
        axios.get('/listings/host/my-listings'),
        axios.get('/bookings?type=host')
      ]);

      const hostListings = listingsRes.data.listings || [];
      const hostBookings = bookingsRes.data.bookings || [];

      setListings(hostListings);
      setBookings(hostBookings);

      // Calculate stats
      const totalEarnings = hostBookings
        .filter(booking => booking.status === 'completed' || booking.status === 'confirmed')
        .reduce((total, booking) => total + booking.totalAmount, 0);

      const ratingsSum = hostListings.reduce((sum, listing) => 
        sum + (listing.rating.average * listing.rating.count), 0);
      const totalRatings = hostListings.reduce((sum, listing) => 
        sum + listing.rating.count, 0);
      const averageRating = totalRatings > 0 ? ratingsSum / totalRatings : 0;

      setStats({
        totalListings: hostListings.length,
        totalBookings: hostBookings.length,
        totalEarnings,
        averageRating
      });
    } catch (error) {
      console.error('Failed to fetch host data:', error);
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const deleteListing = async (listingId) => {
    if (!confirm('Are you sure you want to delete this listing?')) {
      return;
    }

    try {
      await axios.delete(`/listings/${listingId}`);
      toast.success('Listing deleted successfully');
      fetchHostData();
    } catch (error) {
      const message = error.response?.data?.message || 'Failed to delete listing';
      toast.error(message);
    }
  };

  const recentBookings = bookings.slice(0, 5);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">
              Welcome back, {user?.firstName}!
            </h1>
            <p className="text-gray-600">
              Manage your properties and track your hosting performance
            </p>
          </div>
          <Link
            to="/host/create-listing"
            className="inline-flex items-center space-x-2 bg-primary-600 text-white px-6 py-3 rounded-lg hover:bg-primary-700 transition-colors mt-4 md:mt-0"
          >
            <Plus size={20} />
            <span>Add New Listing</span>
          </Link>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-xl p-6 shadow-soft">
            <div className="flex items-center">
              <div className="bg-blue-100 p-3 rounded-lg">
                <Home className="text-blue-600" size={24} />
              </div>
              <div className="ml-4">
                <p className="text-sm text-gray-600">Total Properties</p>
                <p className="text-2xl font-bold">{stats.totalListings}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl p-6 shadow-soft">
            <div className="flex items-center">
              <div className="bg-green-100 p-3 rounded-lg">
                <Calendar className="text-green-600" size={24} />
              </div>
              <div className="ml-4">
                <p className="text-sm text-gray-600">Total Bookings</p>
                <p className="text-2xl font-bold">{stats.totalBookings}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl p-6 shadow-soft">
            <div className="flex items-center">
              <div className="bg-yellow-100 p-3 rounded-lg">
                <DollarSign className="text-yellow-600" size={24} />
              </div>
              <div className="ml-4">
                <p className="text-sm text-gray-600">Total Earnings</p>
                <p className="text-2xl font-bold">${stats.totalEarnings.toLocaleString()}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl p-6 shadow-soft">
            <div className="flex items-center">
              <div className="bg-purple-100 p-3 rounded-lg">
                <Star className="text-purple-600" size={24} />
              </div>
              <div className="ml-4">
                <p className="text-sm text-gray-600">Average Rating</p>
                <p className="text-2xl font-bold">
                  {stats.averageRating > 0 ? stats.averageRating.toFixed(1) : '-'}
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* My Properties */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-xl shadow-soft">
              <div className="p-6 border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <h2 className="text-xl font-semibold">My Properties</h2>
                  <Link
                    to="/host/create-listing"
                    className="text-primary-600 hover:text-primary-700 text-sm font-medium"
                  >
                    Add New
                  </Link>
                </div>
              </div>

              <div className="divide-y divide-gray-200">
                {listings.length === 0 ? (
                  <div className="p-12 text-center">
                    <Home className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                    <h3 className="text-lg font-medium text-gray-900 mb-2">No properties yet</h3>
                    <p className="text-gray-600 mb-6">
                      Start earning by listing your first property
                    </p>
                    <Link
                      to="/host/create-listing"
                      className="inline-flex items-center bg-primary-600 text-white px-6 py-3 rounded-lg hover:bg-primary-700 transition-colors"
                    >
                      <Plus size={20} className="mr-2" />
                      Create First Listing
                    </Link>
                  </div>
                ) : (
                  listings.map(listing => (
                    <div key={listing._id} className="p-6">
                      <div className="flex items-start space-x-4">
                        <img
                          src={listing.images?.[0]?.url || '/images/placeholder-property.jpg'}
                          alt={listing.title}
                          className="w-20 h-20 rounded-lg object-cover"
                        />
                        
                        <div className="flex-1 min-w-0">
                          <h3 className="font-semibold text-gray-900 truncate">
                            {listing.title}
                          </h3>
                          <p className="text-gray-600 text-sm mb-2">
                            {listing.location.city}, {listing.location.state}
                          </p>
                          
                          <div className="flex items-center space-x-4 text-sm text-gray-500">
                            <span>${listing.price}/night</span>
                            <span>•</span>
                            <span>{listing.maxGuests} guests</span>
                            {listing.rating.count > 0 && (
                              <>
                                <span>•</span>
                                <div className="flex items-center">
                                  <Star size={14} className="text-yellow-400 fill-current mr-1" />
                                  <span>{listing.rating.average.toFixed(1)}</span>
                                </div>
                              </>
                            )}
                          </div>
                        </div>

                        <div className="flex items-center space-x-2">
                          <Link
                            to={`/listings/${listing._id}`}
                            className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
                            title="View"
                          >
                            <Eye size={18} />
                          </Link>
                          <button
                            className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
                            title="Edit"
                          >
                            <Edit size={18} />
                          </button>
                          <button
                            onClick={() => deleteListing(listing._id)}
                            className="p-2 text-gray-400 hover:text-red-600 transition-colors"
                            title="Delete"
                          >
                            <Trash2 size={18} />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>

          {/* Recent Bookings */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-xl shadow-soft">
              <div className="p-6 border-b border-gray-200">
                <h2 className="text-xl font-semibold">Recent Bookings</h2>
              </div>

              <div className="divide-y divide-gray-200">
                {recentBookings.length === 0 ? (
                  <div className="p-6 text-center">
                    <Calendar className="mx-auto h-8 w-8 text-gray-400 mb-2" />
                    <p className="text-gray-600 text-sm">No bookings yet</p>
                  </div>
                ) : (
                  recentBookings.map(booking => (
                    <div key={booking._id} className="p-4">
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-medium text-sm">
                          {booking.guest.firstName} {booking.guest.lastName}
                        </span>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          booking.status === 'confirmed' ? 'bg-green-100 text-green-800' :
                          booking.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                          booking.status === 'cancelled' ? 'bg-red-100 text-red-800' :
                          'bg-blue-100 text-blue-800'
                        }`}>
                          {booking.status}
                        </span>
                      </div>
                      
                      <p className="text-gray-600 text-xs truncate mb-1">
                        {booking.listing.title}
                      </p>
                      
                      <div className="flex items-center justify-between text-xs text-gray-500">
                        <span>
                          {new Date(booking.checkIn).toLocaleDateString()}
                        </span>
                        <span className="font-medium">${booking.totalAmount}</span>
                      </div>
                    </div>
                  ))
                )}
              </div>

              {bookings.length > 5 && (
                <div className="p-4 border-t border-gray-200">
                  <Link
                    to="/bookings?type=host"
                    className="w-full bg-gray-50 text-gray-700 px-4 py-2 rounded-lg text-center hover:bg-gray-100 transition-colors block"
                  >
                    View All Bookings
                  </Link>
                </div>
              )}
            </div>

            {/* Quick Stats */}
            <div className="bg-white rounded-xl shadow-soft mt-6">
              <div className="p-6 border-b border-gray-200">
                <h2 className="text-xl font-semibold">This Month</h2>
              </div>
              
              <div className="p-6 space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-gray-600">New Bookings</span>
                  <span className="font-semibold">
                    {bookings.filter(b => {
                      const bookingDate = new Date(b.createdAt);
                      const now = new Date();
                      return bookingDate.getMonth() === now.getMonth() && 
                             bookingDate.getFullYear() === now.getFullYear();
                    }).length}
                  </span>
                </div>
                
                <div className="flex items-center justify-between">
                  <span className="text-gray-600">Revenue</span>
                  <span className="font-semibold">
                    ${bookings
                      .filter(b => {
                        const bookingDate = new Date(b.createdAt);
                        const now = new Date();
                        return bookingDate.getMonth() === now.getMonth() && 
                               bookingDate.getFullYear() === now.getFullYear() &&
                               (b.status === 'completed' || b.status === 'confirmed');
                      })
                      .reduce((sum, b) => sum + b.totalAmount, 0)
                      .toLocaleString()
                    }
                  </span>
                </div>
                
                <div className="flex items-center justify-between">
                  <span className="text-gray-600">Occupancy Rate</span>
                  <div className="flex items-center">
                    <span className="font-semibold mr-1">85%</span>
                    <TrendingUp size={16} className="text-green-500" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HostDashboard;
